import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { axiosInstance } from '../../App';
import {
  Clock,
  MapPin,
  Calendar,
  CheckCircle,
  FileText,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Wrench,
  User,
  Phone,
  Mail
} from 'lucide-react';
import { formatDate, formatDateTime, formatTime } from '../../utils/dateUtils';

export default function MyRequests({ customerData, userEmail }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedLead, setExpandedLead] = useState(null);
  const [leadDetails, setLeadDetails] = useState({});

  useEffect(() => {
    if (userEmail) {
      fetchLeads();
    }
  }, [userEmail]);

  const fetchLeads = async () => {
    try {
      const response = await axiosInstance.get(`/leads?customer_email=${encodeURIComponent(userEmail)}`);
      setLeads(response.data);
    } catch (error) {
      toast.error('Failed to load service requests');
    } finally {
      setLoading(false);
    }
  };

  const fetchLeadDetails = async (leadId) => {
    try {
      const response = await axiosInstance.get(`/leads/${leadId}/details`);
      setLeadDetails(prev => ({ ...prev, [leadId]: response.data }));
    } catch (error) {
      console.error('Failed to load lead details:', error);
    }
  };

  const toggleLeadExpand = async (leadId) => {
    if (expandedLead === leadId) {
      setExpandedLead(null);
    } else {
      setExpandedLead(leadId);
      if (!leadDetails[leadId]) {
        await fetchLeadDetails(leadId);
      }
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-500',
      contacted: 'bg-indigo-500',
      visit_scheduled: 'bg-purple-500',
      quote_sent: 'bg-orange-500',
      quote_approved: 'bg-teal-500',
      converted: 'bg-green-500',
      cancelled: 'bg-red-500',
      lost: 'bg-gray-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  const getStatusLabel = (status) => {
    const labels = {
      new: 'New Request',
      contacted: 'Being Reviewed',
      visit_scheduled: 'Site Visit Scheduled',
      quote_sent: 'Quote Pending',
      quote_approved: 'Quote Approved',
      converted: 'Converted to Job',
      cancelled: 'Cancelled',
      lost: 'Closed'
    };
    return labels[status] || status.replace('_', ' ').toUpperCase();
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', { timeZone: 'America/Phoenix',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  // Separate active and completed leads
  const activeLeads = leads.filter(l => !['converted', 'cancelled', 'lost'].includes(l.status));
  const completedLeads = leads.filter(l => ['converted', 'cancelled', 'lost'].includes(l.status));

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          My Service Requests
        </h2>
        <p className="text-gray-600">Track your service inquiries and quotes</p>
      </div>

      {/* Active Requests */}
      {activeLeads.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <Clock className="h-5 w-5 text-blue-500" />
            Active Requests ({activeLeads.length})
          </h3>
          {activeLeads.map((lead) => (
            <Card key={lead.id} className="backdrop-blur-sm bg-white/90 shadow-lg border-0 overflow-hidden">
              <CardHeader 
                className="cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => toggleLeadExpand(lead.id)}
              >
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <FileText className="h-5 w-5 text-teal-600" />
                      {lead.lead_data?.service_needed || lead.form_name || 'Service Request'}
                    </CardTitle>
                    <CardDescription className="flex items-center gap-4">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        Submitted: {formatDateTime(lead.created_at)}
                      </span>
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className={`${getStatusColor(lead.status)} text-white`}>
                      {getStatusLabel(lead.status)}
                    </Badge>
                    {expandedLead === lead.id ? <ChevronUp /> : <ChevronDown />}
                  </div>
                </div>
              </CardHeader>

              {expandedLead === lead.id && (
                <CardContent className="border-t bg-gray-50/50">
                  <div className="space-y-4 pt-4">
                    {/* Request Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-white p-3 rounded-lg shadow-sm">
                        <div className="text-xs text-gray-500 mb-1">Service Requested</div>
                        <div className="font-medium flex items-center gap-2">
                          <Wrench className="h-4 w-4 text-teal-500" />
                          {lead.lead_data?.service_needed || 'Not specified'}
                        </div>
                      </div>
                      {lead.lead_data?.address && (
                        <div className="bg-white p-3 rounded-lg shadow-sm">
                          <div className="text-xs text-gray-500 mb-1">Location</div>
                          <div className="font-medium flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-blue-500" />
                            {lead.lead_data.address}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Visit Info */}
                    {leadDetails[lead.id]?.visits && leadDetails[lead.id].visits.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
                          <Calendar className="h-4 w-4" />
                          Scheduled Visit
                        </h4>
                        {leadDetails[lead.id].visits.map((visit, idx) => (
                          <div key={idx} className="bg-white p-3 rounded-lg shadow-sm border-l-4 border-purple-500 space-y-2">
                            <div className="flex justify-between items-center">
                              <div>
                                <div className="font-medium">{formatDateTime(visit.scheduled_date)}</div>
                                {visit.handyman_name && (
                                  <div className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                                    <User className="h-3 w-3" />
                                    Technician: {visit.handyman_name}
                                  </div>
                                )}
                              </div>
                              <Badge className={visit.status === 'completed' ? 'bg-green-500' : 'bg-purple-500'}>
                                {visit.status?.toUpperCase() || 'SCHEDULED'}
                              </Badge>
                            </div>
                            {/* Visit Report */}
                            {visit.visit_report && (
                              <div className="mt-2 pt-2 border-t">
                                <p className="text-xs font-semibold text-gray-700 mb-1">Inspection Report:</p>
                                <div className="space-y-1">
                                  {visit.visit_report.line_items?.map((item, i) => (
                                    <div key={i} className="text-sm bg-gray-50 p-2 rounded">
                                      <p className="font-medium">{item.description}</p>
                                      {item.notes && <p className="text-xs text-gray-500">{item.notes}</p>}
                                      {item.photos?.length > 0 && (
                                        <div className="flex gap-1 mt-1">
                                          {item.photos.map((photo, pi) => (
                                            <img key={pi} src={photo} alt="" className="w-10 h-10 object-cover rounded cursor-pointer" onClick={() => window.open(photo, '_blank')} />
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                  {visit.visit_report.general_notes && (
                                    <p className="text-xs text-gray-500 italic mt-1">Notes: {visit.visit_report.general_notes}</p>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Quote Info */}
                    {leadDetails[lead.id]?.quotes && leadDetails[lead.id].quotes.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          Quote
                        </h4>
                        {leadDetails[lead.id].quotes.map((quote, idx) => (
                          <div key={idx} className="bg-white p-3 rounded-lg shadow-sm border-l-4 border-orange-500">
                            <div className="flex justify-between items-center">
                              <div>
                                <div className="text-2xl font-bold text-teal-600">${quote.total_amount}</div>
                                <div className="text-sm text-gray-600">
                                  {quote.items?.length || 0} item(s)
                                </div>
                              </div>
                              <Badge className={
                                quote.status === 'approved' ? 'bg-green-500' :
                                quote.status === 'rejected' ? 'bg-red-500' :
                                'bg-orange-500'
                              }>
                                {quote.status?.toUpperCase() || 'PENDING'}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Status Timeline */}
                    <div className="text-sm text-gray-500 pt-2 border-t">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        <span>Last updated: {formatDateTime(lead.updated_at)}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Completed/Converted Requests */}
      {completedLeads.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-500" />
            Past Requests ({completedLeads.length})
          </h3>
          {completedLeads.map((lead) => (
            <Card key={lead.id} className="backdrop-blur-sm bg-white/80 shadow-lg border-0">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <CardTitle className="text-lg flex items-center gap-2 text-gray-600">
                      <FileText className="h-5 w-5" />
                      {lead.lead_data?.service_needed || lead.form_name || 'Service Request'}
                    </CardTitle>
                    <CardDescription>
                      {formatDateTime(lead.created_at)}
                    </CardDescription>
                  </div>
                  <Badge className={`${getStatusColor(lead.status)} text-white`}>
                    {getStatusLabel(lead.status)}
                  </Badge>
                </div>
              </CardHeader>
            </Card>
          ))}
        </div>
      )}

      {/* Empty State */}
      {leads.length === 0 && (
        <Card className="backdrop-blur-sm bg-white/80 shadow-lg border-0">
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-700 mb-2">No Service Requests Yet</h3>
            <p className="text-gray-500">Submit a service request to get started.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
